import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../services/api';
import { toast } from 'react-toastify';
import { PlusIcon, PencilIcon, XMarkIcon, KeyIcon } from '@heroicons/react/24/outline';

const fetchClients = () => api.get('/api-clients').then(res => res.data.data.clients);

export default function ApiClients() {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);

  const [form, setForm] = useState({
    name: '',
    status: 'active',
    clientId: '',
    clientSecret: '',
    allowedIPs: '',
    rateLimit: 1000,
    tokenExpiry: 3600
  });

  const { data: clients, isLoading } = useQuery('apiClients', fetchClients);

  const saveClient = useMutation(
    (data) => {
      const payload = {
        ...data,
        allowedIPs: typeof data.allowedIPs === 'string' ? data.allowedIPs.split(',').map(s => s.trim()).filter(s => s) : data.allowedIPs
      };
      if (!payload.clientId) delete payload.clientId;
      if (!payload.clientSecret) delete payload.clientSecret;
      if (editingClient) {
        return api.put(`/api-clients/${editingClient.id}`, payload);
      } else {
        return api.post('/api-clients', payload);
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('apiClients');
        toast.success(editingClient ? 'Client updated' : 'Client created');
        closeModal();
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || 'Failed to save client');
      }
    }
  );

  const openModal = (client = null) => {
    if (client) {
      setEditingClient(client);
      setForm({
        name: client.name,
        status: client.status,
        clientId: client.clientId || '',
        clientSecret: client.clientSecret || '',
        allowedIPs: Array.isArray(client.allowedIPs) ? client.allowedIPs.join(', ') : '',
        rateLimit: client.rateLimit || 1000,
        tokenExpiry: client.tokenExpiry || 3600
      });
    } else {
      setEditingClient(null);
      setForm({ name: '', clientId: '', clientSecret: '', status: 'active', allowedIPs: '', rateLimit: 1000, tokenExpiry: 3600 });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => setIsModalOpen(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    saveClient.mutate(form);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">API Clients</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Manage external API integrations and bank configurations</p>
        </div>
        <button onClick={() => openModal()} className="btn-primary">
          <PlusIcon className="h-4 w-4 mr-2" />
          Add Client
        </button>
      </div>

      <div className="card">
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Client ID</th>
                <th>Status</th>
                <th>Rate Limit</th>
                <th>Last Used</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan="6" className="text-center py-4">Loading...</td></tr>
              ) : clients?.length === 0 ? (
                <tr><td colSpan="6" className="text-center py-4">No API clients found</td></tr>
              ) : (
                clients?.map((client) => (
                  <tr key={client.id} className="hover:bg-slate-50 dark:hover:bg-slate-800">
                    <td className="font-medium text-slate-900 dark:text-white">{client.name}</td>
                    <td className="font-mono text-xs">{client.clientId}</td>
                    <td>
                      <span className={`badge ${client.status === 'active' ? 'badge-green' : 'badge-red'}`}>
                        {client.status}
                      </span>
                    </td>
                    <td>{client.rateLimit} / hr</td>
                    <td>{client.lastUsedAt ? new Date(client.lastUsedAt).toLocaleString() : 'Never'}</td>
                    <td>
                      <button onClick={() => openModal(client)} className="text-indigo-600 hover:text-indigo-900 p-1">
                        <PencilIcon className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-900 rounded-lg shadow-xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95">
            <div className="px-6 py-4 flex justify-between items-center border-b border-slate-200 dark:border-slate-800">
              <h3 className="text-lg font-semibold">{editingClient ? 'Edit API Client' : 'New API Client'}</h3>
              <button onClick={closeModal} className="p-1 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-full">
                <XMarkIcon className="h-5 w-5 text-slate-500" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1 dark:text-slate-200">Name</label>
                <input type="text" className="input-field" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} pattern="^[a-zA-Z0-9\s\-_.]+$" title="Only alphanumeric characters, spaces, hyphens, underscores and dots" placeholder="e.g. Meezan Bank" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1 dark:text-slate-200">Client ID</label>
                  <input type="text" className="input-field font-mono text-xs" required value={form.clientId} onChange={e => setForm({ ...form, clientId: e.target.value })} pattern="^[a-zA-Z0-9_\-]+$" title="Only alphanumeric characters, hyphens and underscores" placeholder="Enter Client ID" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 dark:text-slate-200">Client Secret</label>
                  <input type="text" className="input-field font-mono text-xs" required={!editingClient} minLength="8" value={form.clientSecret} onChange={e => setForm({ ...form, clientSecret: e.target.value })} placeholder={editingClient ? "Leave empty to keep current" : "Enter Client Secret"} />
                </div>
              </div>

              {editingClient && (
                <div>
                  <label className="block text-sm font-medium mb-1 dark:text-slate-200">Status</label>
                  <select className="input-field" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-1 dark:text-slate-200">Allowed IPs (Comma separated)</label>
                <input type="text" className="input-field" value={form.allowedIPs} onChange={e => setForm({ ...form, allowedIPs: e.target.value })} placeholder="e.g. 192.168.1.1, 10.0.0.1" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1 dark:text-slate-200">Rate Limit (per hr)</label>
                  <input type="number" className="input-field" value={form.rateLimit} onChange={e => setForm({ ...form, rateLimit: parseInt(e.target.value) })} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 dark:text-slate-200">Token Expiry (sec)</label>
                  <input type="number" className="input-field" value={form.tokenExpiry} onChange={e => setForm({ ...form, tokenExpiry: parseInt(e.target.value) })} />
                </div>
              </div>

              {editingClient && (
                <div className="bg-slate-50 dark:bg-slate-800 p-3 rounded-md border border-slate-200 dark:border-slate-700 mt-4 text-xs font-mono space-y-2">
                  <div className="flex justify-between items-center text-[10px]">
                    <span className="text-slate-500 flex items-center"><KeyIcon className="h-3 w-3 mr-1" /> Current Secret:</span>
                    <span className="text-slate-700 font-bold blur-[3px] hover:blur-none transition-all cursor-pointer select-all">{editingClient.clientSecret || '•••••••• (Encrypted)'}</span>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 dark:border-slate-800">
                <button type="button" onClick={closeModal} className="btn-secondary">Cancel</button>
                <button type="submit" disabled={saveClient.isLoading} className="btn-primary">
                  {saveClient.isLoading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
